"use client";

import { cn } from "@marketing-sites/utils/cn";
import { Text } from "@/components/atoms/text";
import { triggerAnalyticsLinkClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { ElementVisibleAnalytics } from "@marketing-sites/analytics/use-element-visible-analytics";
import ArrowUpRight from "@/icons/arrow-up-right.svg";
import type { LearningCenterEducationalResource } from "@marketing-sites/sanity-client";
import { Img } from "@/components/atoms/img";
import { useLocalizedHref } from "@/lib/l10n/providers";
import { normalizeAnalyticsKey } from "@marketing-sites/analytics/normalize-analytics-key";

export const QuickLinkCard = ({
  resource,
}: {
  resource: LearningCenterEducationalResource;
}) => {
  const localizeHref = useLocalizedHref();

  return (
    <div
      className={cn(
        "group relative flex flex-col gap-2 border border-black/10 p-5",
        "transition-colors hover:border-black/30",
        "flex justify-between",
      )}
    >
      <div>
        <div className="flex items-start justify-between gap-2">
          <div className="w-fit">
            <Text
              size="h5"
              className={cn(
                "inline max-w-[563px] bg-gradient-to-r from-current to-current bg-[length:0%_0.06em] bg-[position:100%_100%] bg-no-repeat pb-0.5 font-medium transition-[background-size] duration-200 ease-out group-hover:bg-[length:100%_0.06em] group-hover:bg-[position:0%_100%]",
                "text-[1.125rem] md:text-[1.25rem] xl:text-[1.5rem]",
              )}
              asChild
            >
              <h3>
                <a
                  href={localizeHref(resource.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="after:absolute after:inset-0 after:z-10"
                  onClick={() => {
                    triggerAnalyticsLinkClick({
                      text: resource.title,
                      // eslint-disable-next-line analytics/id-must-be-static -- _id is the resource's Sanity document id, not copy; one document set serves every locale
                      analyticsId: `link_learning_center_quick_card_${normalizeAnalyticsKey(resource._id)}`,
                      location: "learning-center-quick-links",
                      destination: resource.url,
                    });
                  }}
                >
                  {resource.title}
                </a>
              </h3>
            </Text>
          </div>
          <ArrowUpRight className="mt-0.5 h-3 w-3 shrink-0 fill-current opacity-40 transition-opacity group-hover:opacity-100" />
        </div>
        {resource.excerpt && (
          <Text asChild className="max-w-[563px] text-sm text-black/80">
            <p>{resource.excerpt}</p>
          </Text>
        )}
      </div>

      <ElementVisibleAnalytics
        trackingData={{
          // eslint-disable-next-line analytics/id-must-be-static -- _id is the resource's Sanity document id, not copy; one document set serves every locale
          analyticsId: `evt_learning_center_quick_link_card_visible_${normalizeAnalyticsKey(resource._id)}`,
          location: "Learning Center Quick Links",
          text: resource.title,
        }}
      />
      <div className="relative aspect-[411/249] w-full">
        {resource.mainImage?.asset?.url && (
          <Img.Client
            src={resource.mainImage?.asset?.url}
            alt={resource.mainImage?.alt ?? resource.title}
            width={resource.mainImage?.asset?.metadata?.dimensions?.width}
            height={resource.mainImage?.asset?.metadata?.dimensions?.height}
            className="object-cover"
            fill
          />
        )}
      </div>
    </div>
  );
};
